import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import FaceCaptureModal from './FaceCaptureModal';
import {
  getUserFacePhotos,
  enrollFacePhotos,
  deleteFacePhotos,
  fetchFacePhotoUrl,
} from '../services/faceApi';
import { describeImageFile } from '../services/faceEngine';
import './FacePhotosModal.css';

/**
 * Gestão das fotos faciais de uma pessoa: ver as fotos atuais, recadastrar
 * pela câmera (3 fotos guiadas), enviar 3 arquivos de imagem (admin) ou
 * excluir o cadastro.
 *
 * props:
 *  - person: { id, name }
 *  - canDelete: mostra o botão de excluir (admin)
 *  - onChanged: chamado após cadastrar/excluir (para o pai atualizar badges)
 *  - onClose
 */
function FacePhotosModal({ person, canDelete = false, onChanged, onClose }) {
  const [photos, setPhotos] = useState(null); // null = carregando
  const [photoUrls, setPhotoUrls] = useState({});
  const [showCamera, setShowCamera] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInputRef = useRef(null);
  const urlsRef = useRef([]);

  // Flag de montagem: URLs criados por fetches que resolvem DEPOIS do unmount
  // são revogados na hora (senão o cleanup, que roda uma vez, nunca os pega).
  const mountedRef = useRef(true);

  const loadPhotos = useCallback(async () => {
    try {
      const data = await getUserFacePhotos(person.id);
      if (!mountedRef.current) return;
      setPhotos(data);
      const urls = {};
      for (const photo of data) {
        try {
          const url = await fetchFacePhotoUrl(photo.id);
          if (!mountedRef.current) {
            URL.revokeObjectURL(url);
            return;
          }
          urls[photo.id] = url;
          urlsRef.current.push(url);
        } catch (e) {
          /* thumbnail indisponível não impede a tela */
        }
      }
      setPhotoUrls(urls);
    } catch (error) {
      if (!mountedRef.current) return;
      toast.error(
        'Erro ao carregar fotos: ' +
          (error.response?.data?.message || error.message),
      );
      setPhotos([]);
    }
  }, [person.id]);

  useEffect(() => {
    mountedRef.current = true;
    loadPhotos();
    const urls = urlsRef.current;
    return () => {
      mountedRef.current = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [loadPhotos]);

  const handleEnrollComplete = async (captures) => {
    setShowCamera(false);
    setSaving(true);
    try {
      await enrollFacePhotos(person.id, captures);
      toast.success(`Fotos faciais de ${person.name} cadastradas!`);
      await loadPhotos();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(
        'Erro ao salvar fotos: ' +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 3);
    event.target.value = '';
    if (files.length === 0) return;
    setSaving(true);
    try {
      const captures = [];
      for (const file of files) {
        // describeImageFile devolve o descritor da imagem original + um JPEG
        // recomprimido (≤640px): manda o blob leve, não o arquivo de MB.
        const { descriptor, blob } = await describeImageFile(file);
        captures.push({ blob, descriptor });
      }
      await enrollFacePhotos(person.id, captures);
      toast.success(
        `${captures.length} foto(s) de ${person.name} cadastrada(s) a partir de arquivo!`,
      );
      await loadPhotos();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(
        'Erro ao processar imagens: ' +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteFacePhotos(person.id);
      toast.success(`Fotos faciais de ${person.name} removidas.`);
      setConfirmDelete(false);
      await loadPhotos();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(
        'Erro ao remover fotos: ' +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setSaving(false);
    }
  };

  if (showCamera) {
    return (
      <FaceCaptureModal
        mode="enroll"
        personName={person.name}
        // A captura das 3 fotos é automática: escolher a câmera antes evita
        // cadastrar o rosto errado (ou a parede) e ter que refazer tudo.
        chooseCamera
        onComplete={handleEnrollComplete}
        onClose={() => setShowCamera(false)}
      />
    );
  }

  return (
    <div className="fp-overlay" onClick={onClose}>
      <div className="fp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fp-header">
          <h2>Fotos faciais — {person.name}</h2>
          <button type="button" className="fp-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className="fp-body">
          {photos === null ? (
            <div className="fp-loading">Carregando fotos...</div>
          ) : photos.length === 0 ? (
            <div className="fp-empty">
              <p>Nenhuma foto facial cadastrada.</p>
              <p className="fp-hint">
                Cadastre 3 fotos para habilitar a verificação de identidade na
                entrada e na saída.
              </p>
            </div>
          ) : (
            <div className="fp-grid">
              {photos.map((photo, i) => (
                <figure key={photo.id} className="fp-photo">
                  {photoUrls[photo.id] ? (
                    <img src={photoUrls[photo.id]} alt={`Foto ${i + 1} de ${person.name}`} />
                  ) : (
                    <div className="fp-photo-missing">sem imagem</div>
                  )}
                  <figcaption>
                    Foto {i + 1}
                    {photo.capturedByName ? ` — por ${photo.capturedByName}` : ''}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}

          {confirmDelete && (
            <div className="fp-confirm">
              <p>
                Remover TODAS as fotos faciais de <strong>{person.name}</strong>?
                A verificação de identidade deixa de funcionar até novo cadastro.
              </p>
              <div className="fp-confirm-actions">
                <button type="button" className="btn-danger" onClick={handleDelete} disabled={saving}>
                  {saving ? 'Removendo...' : 'Sim, remover'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setConfirmDelete(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="fp-footer">
          {canDelete && photos && photos.length > 0 && !confirmDelete && (
            <button
              type="button"
              className="fp-delete-link"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
            >
              Excluir fotos
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            style={{ display: 'none' }}
            onChange={handleFilesSelected}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
          >
            {saving ? 'Processando...' : 'Enviar arquivos'}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowCamera(true)}
            disabled={saving}
          >
            {photos && photos.length > 0 ? 'Recadastrar pela câmera' : 'Cadastrar pela câmera'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FacePhotosModal;
